import React, { useMemo, useRef, useCallback } from "react";

type HeaderTab = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number;
};

type ModernPageHeaderProps = {
  title: string;
  tabs?: HeaderTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  actions?: React.ReactNode;
  backButton?: React.ReactNode;
  hideBottomBorder?: boolean;
};

const ModernPageHeader: React.FC<ModernPageHeaderProps> = ({
  title,
  tabs = [],
  activeTab,
  onTabChange,
  actions,
  backButton,
  hideBottomBorder = false,
}) => {
  // Keyboard navigation for tabs (ArrowLeft/ArrowRight)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const currentIndex = useMemo(
    () => tabs.findIndex((t) => t.id === activeTab),
    [tabs, activeTab],
  );

  const focusTab = useCallback((index: number) => {
    const el = tabRefs.current[index];
    if (el) el.focus();
  }, []);

  const onTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const next = (index + 1) % tabs.length;
        focusTab(next);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prev = (index - 1 + tabs.length) % tabs.length;
        focusTab(prev);
      }
    },
    [tabs.length, focusTab],
  );

  return (
    <header className="bg-white">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {backButton && backButton}
          <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        </div>

        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {tabs.length > 0 && (
        <div className="px-4 sm:px-6">
          <nav
            role="tablist"
            aria-orientation="horizontal"
            className="-mb-px flex overflow-x-auto no-scrollbar border-b border-gray-200"
          >
            {tabs.map((tab, idx) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={
                    isActive || (activeTab == null && idx === 0) ? 0 : -1
                  }
                  ref={(el) => (tabRefs.current[idx] = el)}
                  onKeyDown={(e) => onTabKeyDown(e, idx)}
                  onClick={() => onTabChange && onTabChange(tab.id)}
                  className={[
                    "relative inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-t-md",
                    isActive
                      ? "text-primary border-b-2 border-primary"
                      : "text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300",
                  ].join(" ")}
                >
                  {tab.icon && <span className="mr-1.5">{tab.icon}</span>}
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span
                      className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700"
                      aria-label={`${tab.badge} items`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {!hideBottomBorder && <div className="border-b border-gray-200" />}
    </header>
  );
};

export default ModernPageHeader;
